/*
 * Sticky add-to-cart — Primal Kiwi (Stiletto/Fluorescent).
 *
 * Visibility: appears when the main ATC (#qun_add) scrolls out of view, gated by an
 * optional per-device scroll-depth threshold (vh). Hides while the quick-cart is open.
 *
 * Add to cart: POST /cart/add.js with the selected variant, then drive the theme's own
 * quick-cart — dispatch `apps:product-added-to-cart` (refresh) and click the header cart
 * icon `a[data-js-cart-icon]` (opens the drawer). No dependency on Dawn cart APIs.
 */
(() => {
  'use strict';

  const LABELS = {
    select: 'Select options',
    add: 'Add to Cart',
    soldout: 'Sold Out',
    adding: 'Adding…',
    added: 'Added!'
  };

  class StickyATC {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.section;
      this.expanded = false;
      this._wantsVisible = false;

      this.panel = el.querySelector('.sticky-atc__panel');
      this.expandBtn = el.querySelector('.sticky-atc__expand-btn');
      this.closeBtn = el.querySelector('.sticky-atc__close-btn');
      this.barAddBtn = el.querySelector('[data-sticky-bar-add]');
      this.panelSubmitBtn = el.querySelector('.sticky-atc__submit');
      this.submitWrap = el.querySelector('.sticky-atc__submit-wrap');
      this.optionBtns = el.querySelectorAll('.sticky-atc__option-btn');

      const variantsEl = document.getElementById(`sticky-atc-variants-${this.sectionId}`);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      this.selectedOptions = {};
      this.selectedVariant = null;
      this.hasChoices = el.dataset.hasChoices === 'true';

      // Seal Subscriptions widget relocation state.
      // The mount point is rendered when the product has selling plans; the actual
      // Seal widget is moved here on expand and restored on collapse so we never
      // own two copies (which would fight Seal's event handlers).
      this.sealMount = el.querySelector('[data-sticky-seal-mount]');
      this.sealNode = null;
      this.sealOriginalParent = null;
      this.sealOriginalNext = null;

      this._thresholdDesktop = parseFloat(el.dataset.scrollDesktop) || 0;
      this._thresholdTablet = parseFloat(el.dataset.scrollTablet) || 0;
      this._thresholdMobile = parseFloat(el.dataset.scrollMobile) || 0;

      // Re-parent to <body> so position:fixed escapes the section stacking context and
      // sits above late-loading third-party widgets.
      document.body.appendChild(this.el);
      setTimeout(() => document.body.appendChild(this.el), 1500);

      this._bindEvents();
      this._observeMainATC();
      this._observeCartDrawer();
      this._bindScrollThreshold();

      // Auto-select for single-variant products; otherwise prompt the shopper.
      const requireVariant = el.dataset.requireVariant !== 'false';
      if (!requireVariant) {
        const defaultId = parseInt(el.dataset.defaultVariant, 10);
        const v = this.variants.find(x => x.id === defaultId)
          || this.variants.find(x => x.available)
          || this.variants[0];
        if (v) this._applyVariant(v);
      } else {
        this._showSelectPrompt();
      }
    }

    _bindEvents() {
      if (this.expandBtn) this.expandBtn.addEventListener('click', () => this._togglePanel());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this._collapsePanel());

      this.optionBtns.forEach(btn => btn.addEventListener('click', () => this._selectOption(btn)));

      if (this.barAddBtn) {
        this.barAddBtn.addEventListener('click', () => {
          // Products with variant or subscription choices open the panel to confirm; simple
          // single-variant products add straight away.
          if (this.hasChoices && !this.expanded) {
            this._expandPanel();
          } else {
            this._submitCart();
          }
        });
      }

      if (this.panelSubmitBtn) {
        this.panelSubmitBtn.addEventListener('click', () => this._submitCart());
      }

      document.addEventListener('click', e => {
        if (this.expanded && !this.el.contains(e.target)) this._collapsePanel();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.expanded) this._collapsePanel();
      });

      // Mirror the main variant picker when it changes (requires window.flu useCustomEvents).
      document.addEventListener('product:variant-change', e => {
        const v = e.detail && e.detail.variant;
        if (!v) return;
        const match = this.variants.find(x => x.id === v.id);
        if (match) this._applyVariant(match);
      });
    }

    _observeMainATC() {
      const mainBtn = document.getElementById('qun_add')
        || document.querySelector(`.product-form-${this.sectionId} [data-add-to-cart]`);
      if (!mainBtn) return;
      new IntersectionObserver(entries => {
        entries.forEach(entry => (entry.isIntersecting ? this._hide() : this._show()));
      }, { threshold: 0 }).observe(mainBtn);
    }

    _observeCartDrawer() {
      const wrapper = document.querySelector('.quick-cart__wrapper');
      if (!wrapper) return;
      new MutationObserver(() => {
        if (wrapper.classList.contains('active')) {
          this._forceHide();
        } else if (this._wantsVisible) {
          this._applyVisibility();
        }
      }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    }

    _cartOpen() {
      const w = document.querySelector('.quick-cart__wrapper');
      return !!(w && w.classList.contains('active'));
    }

    _getScrollThresholdPx() {
      const w = window.innerWidth;
      const vh = w < 750 ? this._thresholdMobile : w < 990 ? this._thresholdTablet : this._thresholdDesktop;
      return (vh / 100) * window.innerHeight;
    }

    _bindScrollThreshold() {
      window.addEventListener('scroll', () => {
        if (!this._wantsVisible || this._cartOpen()) return;
        this._applyVisibility();
      }, { passive: true });
    }

    _applyVisibility() {
      if (window.scrollY >= this._getScrollThresholdPx()) {
        this.el.classList.add('is-visible');
        this.el.setAttribute('aria-hidden', 'false');
      } else {
        this.el.classList.remove('is-visible');
        this.el.setAttribute('aria-hidden', 'true');
      }
    }

    _show() {
      this._wantsVisible = true;
      if (!this._cartOpen()) this._applyVisibility();
    }

    _hide() {
      this._wantsVisible = false;
      this._forceHide();
    }

    _forceHide() {
      this.el.classList.remove('is-visible');
      this.el.setAttribute('aria-hidden', 'true');
      this._collapsePanel();
      // Belt-and-braces: collapsePanel already restores, but if we were force-hidden
      // without going through collapse, make sure the Seal widget is back home.
      this._restoreSeal();
    }

    _togglePanel() {
      this.expanded ? this._collapsePanel() : this._expandPanel();
    }

    _expandPanel() {
      this.expanded = true;
      this.el.classList.add('is-expanded');
      this.panel.removeAttribute('hidden');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'true');
      this._relocateSeal();
    }

    _collapsePanel() {
      this.expanded = false;
      this.el.classList.remove('is-expanded');
      if (this.panel) this.panel.setAttribute('hidden', '');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'false');
      this._restoreSeal();
    }

    // Move Seal's rendered widget into the sticky panel. Single DOM node = same
    // event listeners, same selected radio, same source of truth.
    _relocateSeal() {
      if (!this.sealMount || this.sealNode) return;
      // Clean up any pre-existing duplicates before we choose a node to move.
      this._dedupeSeal();
      // Prefer the inner target element; fall back to the outer container.
      // Match outside the sticky (so we don't grab our own mount point or a
      // previously-relocated node).
      const candidates = document.querySelectorAll('.sealsubs-target-element, .sealsubs-container');
      let node = null;
      for (const c of candidates) {
        if (!this.el.contains(c)) { node = c; break; }
      }
      if (!node) return;
      this.sealNode = node;
      this.sealOriginalParent = node.parentNode;
      this.sealOriginalNext = node.nextSibling;
      this.sealMount.appendChild(node);
      // Seal's app embed may re-inject a fresh widget when it notices ours has
      // left the main form (race on first load, async init). Guard against it.
      this._startSealGuard();
      this._scheduleDedupe(250);
    }

    _restoreSeal() {
      if (!this.sealNode || !this.sealOriginalParent) return;
      if (this.sealOriginalNext && this.sealOriginalNext.parentNode === this.sealOriginalParent) {
        this.sealOriginalParent.insertBefore(this.sealNode, this.sealOriginalNext);
      } else {
        this.sealOriginalParent.appendChild(this.sealNode);
      }
      // Dedupe BEFORE clearing the tracked ref, so we know which widget to keep.
      this._dedupeSeal();
      this.sealNode = null;
      this.sealOriginalParent = null;
      this.sealOriginalNext = null;
      this._stopSealGuard();
      // One last sweep in case Seal injects right after we hand back the DOM.
      this._scheduleDedupe(300);
    }

    // Watches the document while our widget is in the sticky. If Seal re-injects
    // a duplicate, remove it immediately.
    _startSealGuard() {
      if (this._sealGuard) return;
      this._sealGuard = new MutationObserver(() => this._dedupeSeal());
      this._sealGuard.observe(document.body, { childList: true, subtree: true });
    }

    _stopSealGuard() {
      if (!this._sealGuard) return;
      this._sealGuard.disconnect();
      this._sealGuard = null;
    }

    _scheduleDedupe(delay) {
      if (this._dedupeTimer) clearTimeout(this._dedupeTimer);
      this._dedupeTimer = setTimeout(() => {
        this._dedupeSeal();
        this._dedupeTimer = null;
      }, delay || 250);
    }

    // Remove any duplicate Seal widget instances, keeping the one we track
    // (or the first if we don't have a tracked reference).
    _dedupeSeal() {
      const containers = Array.from(document.querySelectorAll('.sealsubs-container'));
      if (containers.length <= 1) return;
      let canonical = null;
      if (this.sealNode) {
        canonical = this.sealNode.classList.contains('sealsubs-container')
          ? this.sealNode
          : (this.sealNode.querySelector('.sealsubs-container')
             || this.sealNode.closest('.sealsubs-container'));
      }
      if (!canonical) canonical = containers[0];
      containers.forEach(c => { if (c !== canonical) c.remove(); });
    }

    // Read the currently-selected selling plan from Seal's UI.
    // Seal renders a 2-tier picker: a radio selects One-time / Subscribe & save,
    // and (under Subscribe & save) a `.sls-select` picks the frequency. The actual
    // selling_plan_id lives on the select, NOT the radio. The selected option
    // container is marked `.sls-active`.
    _getSellingPlanId() {
      const toId = v => {
        if (!v) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? String(n) : null;
      };

      // 1) Within the active option container, the frequency select carries the id.
      const active = document.querySelector('.sls-option-container.sls-active');
      if (active) {
        const sel = active.querySelector('.sls-select');
        const fromSel = sel && toId(sel.value);
        if (fromSel) return fromSel;
        const radio = active.querySelector('.sls-option:checked') || active.querySelector('.sls-option');
        const fromRadio = radio && toId(radio.value);
        if (fromRadio) return fromRadio;
      }

      // 2) Hidden selling_plan input/select on the page (some Seal configs).
      const planInput = document.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
      const fromInput = planInput && toId(planInput.value);
      if (fromInput) return fromInput;

      // 3) Last resort: any checked .sls-option whose value is numeric (single-tier UI).
      const checked = document.querySelector('.sls-option:checked');
      return checked ? toId(checked.value) : null;
    }

    _selectOption(btn) {
      if (btn.hasAttribute('data-unavailable')) return;
      const index = parseInt(btn.dataset.optionIndex, 10);
      this.selectedOptions[index] = btn.dataset.value;
      this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`)
        .forEach(b => b.classList.toggle('is-selected', b === btn));
      const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
      if (display) display.textContent = btn.dataset.value;
      this._applyVariant(this._findVariant());
    }

    _findVariant() {
      return this.variants.find(v => v.options.every((opt, i) => this.selectedOptions[i] === opt)) || null;
    }

    _applyVariant(variant) {
      this.selectedVariant = variant;
      if (variant) {
        variant.options.forEach((value, index) => {
          this.selectedOptions[index] = value;
          this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`)
            .forEach(btn => btn.classList.toggle('is-selected', btn.dataset.value === value));
          const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
          if (display) display.textContent = value;
        });
        this._hideSelectPrompt();
      } else {
        this._showSelectPrompt();
      }

      const available = variant && variant.available;
      [this.panelSubmitBtn, this.barAddBtn].filter(Boolean).forEach(btn => {
        btn.disabled = !available;
        const span = btn.querySelector('span') || btn;
        span.textContent = !variant ? LABELS.select : (available ? LABELS.add : LABELS.soldout);
      });
    }

    _showSelectPrompt() {
      if (!this.submitWrap || this.submitWrap.querySelector('.sticky-atc__select-prompt')) return;
      const p = document.createElement('div');
      p.className = 'sticky-atc__select-prompt';
      p.textContent = LABELS.select;
      this.submitWrap.appendChild(p);
    }

    _hideSelectPrompt() {
      this.el.querySelectorAll('.sticky-atc__select-prompt').forEach(p => p.remove());
    }

    _submitCart() {
      const variant = this.selectedVariant;
      if (!variant || !variant.available) {
        if (this.expandBtn) this._expandPanel();
        return;
      }

      const btns = [this.panelSubmitBtn, this.barAddBtn].filter(Boolean);
      btns.forEach(btn => {
        btn.disabled = true;
        const s = btn.querySelector('span') || btn;
        s.textContent = LABELS.adding;
      });

      const payload = { id: variant.id, quantity: 1 };
      const sellingPlan = this._getSellingPlanId();
      if (sellingPlan) payload.selling_plan = sellingPlan;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      })
        .then(res => {
          if (!res.ok) throw new Error('add-to-cart failed');
          return res.json();
        })
        .then(() => {
          btns.forEach(btn => {
            const s = btn.querySelector('span') || btn;
            s.textContent = LABELS.added;
          });
          // Refresh the quick-cart contents + count, then open the drawer (theme hooks).
          document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
          const cartIcon = document.querySelector('a[data-js-cart-icon]');
          if (cartIcon) cartIcon.click();
          this._collapsePanel();
          setTimeout(() => this._applyVariant(variant), 1800);
        })
        .catch(() => {
          btns.forEach(btn => {
            btn.disabled = false;
            const s = btn.querySelector('span') || btn;
            s.textContent = LABELS.add;
          });
        });
    }
  }

  const init = () => {
    document.querySelectorAll('.sticky-atc').forEach(el => {
      if (el.dataset.stickyInit) return;
      el.dataset.stickyInit = '1';
      new StickyATC(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
